import { StyleSheet, Text, View } from "@react-pdf/renderer";
import {
	consequenceSeverityTable,
	probability_score,
	risk_frequency,
} from "../../../stores/consequence-severity-table";
import { styles as reactPdfStyles } from "../ReactPdfStyles";

const BORDER = "1px solid #000";
const HEADER_BG = "#EFEFEF";

// Tuned to visually match the screenshot layout in PDF.
const TABLE_WIDTH = 780;
const LEFT_IMPACT_COL_WIDTH = 28; // rotated "Impact/Consequence level"
const LEVEL_LETTER_COL_WIDTH = 26; // A-E column

const PROBABILITY_COL_WIDTH = 44; // 5 columns => 220px total (big colored blocks like screenshot)

const HEADER_ROW_1_HEIGHT = 20;
const HEADER_ROW_2_HEIGHT = 50;
const BODY_ROW_HEIGHT = 68;

const styles = StyleSheet.create({
	table: {
		width: TABLE_WIDTH,
		alignSelf: "center",
	},
	row: {
		flexDirection: "row",
	},
	cell: {
		borderRight: BORDER,
		borderBottom: BORDER,
		padding: 3,
		justifyContent: "center",
	},
	headerCell: {
		backgroundColor: HEADER_BG,
	},
	textCenter: {
		textAlign: "center",
	},
	smallText: {
		fontSize: 8.2,
		lineHeight: 1.12,
	},
	extraSmallText: {
		fontSize: 6.5,
		lineHeight: 1.1,
	},
	headerText: {
		fontSize: 9,
		fontWeight: "bold",
	},
	rotate: {
		transform: "rotate(-90deg)",
	},
	riskHigh: { backgroundColor: "#FF4D4D" },
	riskMedium: { backgroundColor: "#FFFB00" },
	riskLow: { backgroundColor: "#00FF75" },
});

function getRiskClass(code: string) {
	if (code === "A") return "LOW";
	if (probability_score.HIGH.includes(code)) return "HIGH";
	if (probability_score.MEDIUM.includes(code)) return "MEDIUM";
	if (probability_score.LOW.includes(code)) return "LOW";
	return "NONE";
}

function getRiskLabel(level: string, frequencyKey: string) {
	// Screenshot shows bottom-left as "A" (not "A1")
	if (level === "A" && String(frequencyKey) === "1") return "A";
	return `${level}${Number(frequencyKey)}`;
}

export default function MyRiskMatrixSection() {
	const levels = ["E", "D", "C", "B", "A"] as const;
	const frequencyEntries = Object.entries(risk_frequency).sort(
		([a], [b]) => Number(a) - Number(b)
	);

	// Consequence columns:
	// [PIHE, Well Control, Discharge HC, Discharge Chemicals, Discharge Contained, Asset damage, Reputation]
	// Widths chosen to match the screenshot proportions while keeping the probability block large.
	const consequencesTotalWidth =
		TABLE_WIDTH -
		LEFT_IMPACT_COL_WIDTH -
		LEVEL_LETTER_COL_WIDTH -
		PROBABILITY_COL_WIDTH * 5;

	const consequenceWidths = [
		72,
		104,
		56,
		68,
		62,
		78,
		consequencesTotalWidth - (72 + 104 + 56 + 68 + 62 + 78),
	];

	const consequenceHeaderLabels = [
		"Personnel injury\nand health\neffects",
		"Well control",
		"Discharge to the environment",
		"", // grouped above (Chemicals handled in the discharge sub-header row)
		"", // grouped above (Contained handled in the discharge sub-header row)
		"Asset damage\n(damage to\nequipment and\nNPT)",
		"Reputation",
	];

	const dischargeStartIdx = 2;
	const dischargeEndIdx = 4;
	const dischargeSubLabels = ["HC", "Chemicals", "Contained"];
	const dischargeGroupWidth = consequenceWidths
		.slice(dischargeStartIdx, dischargeEndIdx + 1)
		.reduce((a, b) => a + b, 0);

	const groupHeader1Width = consequenceWidths.reduce((a, b) => a + b, 0);
	const groupHeader2Width = PROBABILITY_COL_WIDTH * 5;

	return (
		<View style={[{ marginBottom: 15 }]}>
			<Text style={[reactPdfStyles.tableHeader, { marginBottom: 16 }]}>
				Risk matrix
			</Text>
			<View style={{ fontSize: 9 }}>
				<View style={styles.table}>
					{/* Header row 1: group headers */}
					<View style={styles.row}>
						<View
							style={[
								styles.cell,
								{
									width: LEFT_IMPACT_COL_WIDTH,
									height: HEADER_ROW_1_HEIGHT,
									borderRight: 0,
									borderBottom: 0,
									padding: 0,
								},
							]}
						/>
						<View
							style={[
								styles.cell,
								{
									width: LEVEL_LETTER_COL_WIDTH,
									height: HEADER_ROW_1_HEIGHT,
									borderBottom: 0,
									padding: 0,
								},
							]}
						/>

						<View
							style={[
								styles.cell,
								styles.headerCell,
								{
									width: groupHeader1Width,
									height: HEADER_ROW_1_HEIGHT,
									borderTop: BORDER,
								},
							]}
						>
							<Text style={[styles.headerText, styles.textCenter]}>
								Consequence - Type
							</Text>
						</View>
						<View
							style={[
								styles.cell,
								styles.headerCell,
								{
									width: groupHeader2Width,
									height: HEADER_ROW_1_HEIGHT,
									borderTop: BORDER,
								},
							]}
						>
							<Text style={[styles.headerText, styles.textCenter]}>
								Probability/Frequency
							</Text>
						</View>
					</View>

					{/* Header row 2: consequence headers + probability labels */}
					<View style={styles.row}>
						<View
							style={[
								styles.cell,
								{
									width: LEFT_IMPACT_COL_WIDTH,
									height: HEADER_ROW_2_HEIGHT,
									borderRight: 0,
									borderBottom: 0,
									padding: 0,
								},
							]}
						/>
						<View
							style={[
								styles.cell,
								{
									width: LEVEL_LETTER_COL_WIDTH,
									height: HEADER_ROW_2_HEIGHT,
									borderBottom: 0,
									padding: 0,
								},
							]}
						/>

						{/* Consequence headers with merged "Discharge to the environment" */}
						{consequenceHeaderLabels.map((label, idx) => {
							if (idx > dischargeStartIdx && idx <= dischargeEndIdx)
								return null;

							// discharge group spans HC + Chemicals + Contained
							if (idx === dischargeStartIdx) {
								return (
									<View
										key="h-discharge"
										style={[
											// styles.cell,
											styles.headerCell,
											{
												width: dischargeGroupWidth,
												height: HEADER_ROW_2_HEIGHT,
											},
										]}
									>
										<View
											style={[
												styles.cell,
												{ height: HEADER_ROW_2_HEIGHT - HEADER_ROW_1_HEIGHT },
											]}
										>
											<Text style={[styles.headerText, styles.textCenter]}>
												Discharge to the environment
											</Text>
										</View>

										{/* HC, Chemicals and Contained sub-headers */}
										<View style={styles.row}>
											{dischargeSubLabels.map((subLabel, subIdx) => (
												<View
													key={`h-discharge-${subLabel}`}
													style={[
														styles.cell,
														{
															width:
																consequenceWidths[dischargeStartIdx + subIdx],
															height: HEADER_ROW_1_HEIGHT,
														},
													]}
												>
													<Text style={[styles.headerText, styles.textCenter]}>
														{subLabel}
													</Text>
												</View>
											))}
										</View>
									</View>
								);
							}

							return (
								<View
									key={`h-c-${idx}`}
									style={[
										styles.cell,
										styles.headerCell,
										{
											width: consequenceWidths[idx],
											height: HEADER_ROW_2_HEIGHT,
										},
									]}
								>
									<Text style={[styles.headerText, styles.textCenter]}>
										{label}
									</Text>
								</View>
							);
						})}

						{/* Probability headers (number + text) */}
						{frequencyEntries.map(([key, value]) => (
							<View
								key={`h-f-${key}`}
								style={[
									styles.cell,
									styles.headerCell,
									{
										width: PROBABILITY_COL_WIDTH,
										height: HEADER_ROW_2_HEIGHT,
										paddingVertical: 6,
										justifyContent: "flex-start",
									},
								]}
							>
								<Text style={[styles.headerText, styles.textCenter]}>
									{key}
								</Text>
								<Text style={[styles.extraSmallText, styles.textCenter]}>
									{String(value)}
								</Text>
							</View>
						))}
					</View>

					{/* Body: left merged vertical label + 5 rows */}
					<View style={styles.row} wrap={false}>
						{/* Impact/Consequence level column — single cell spanning all rows */}
						<View
							style={[
								styles.cell,
								styles.headerCell,
								{
									width: LEFT_IMPACT_COL_WIDTH,
									height: 5 * BODY_ROW_HEIGHT,
									borderTop: BORDER,
									borderLeft: BORDER,
									borderBottom: BORDER,
									justifyContent: "center",
									alignItems: "center",
								},
							]}
						>
							<View
								style={{
									width: 5 * BODY_ROW_HEIGHT,
									height: LEFT_IMPACT_COL_WIDTH,
									justifyContent: "center",
									alignItems: "center",
									transform: "rotate(-90deg)",
									transformOrigin: "center",
								}}
							>
								<Text
									wrap={false}
									style={[styles.headerText, { textAlign: "center" }]}
								>
									{"Impact/Consequence level"}
								</Text>
							</View>
						</View>

						{/* 5 data rows stacked vertically */}
						<View style={{ flexDirection: "column" }}>
							{levels.map((level, levelIdx) => {
								const isFirstRow = levelIdx === 0;
								return (
									<View key={`r-${level}`} style={styles.row} wrap={false}>
										{/* Level letter */}
										<View
											style={[
												styles.cell,
												styles.headerCell,
												{
													width: LEVEL_LETTER_COL_WIDTH,
													height: BODY_ROW_HEIGHT,
													borderTop: isFirstRow ? BORDER : 0,
												},
											]}
										>
											<Text style={[styles.headerText, styles.textCenter]}>
												{level}
											</Text>
										</View>

										{/* Consequence descriptions */}
										{consequenceSeverityTable.map((item, idx) => (
											<View
												key={`c-${level}-${idx}`}
												style={[
													styles.cell,
													{
														width: consequenceWidths[idx],
														height: BODY_ROW_HEIGHT,
													},
												]}
											>
												<Text style={[styles.smallText, styles.textCenter]}>
													{(item.severity_list as any)[level]}
												</Text>
											</View>
										))}

										{/* Risk matrix */}
										{frequencyEntries.map(([fKey]) => {
											const label = getRiskLabel(level, fKey);
											const cls = getRiskClass(label);
											const bg =
												cls === "HIGH"
													? styles.riskHigh
													: cls === "MEDIUM"
														? styles.riskMedium
														: cls === "LOW"
															? styles.riskLow
															: undefined;

											return (
												<View
													key={`m-${level}-${fKey}`}
													style={[
														styles.cell,
														{
															width: PROBABILITY_COL_WIDTH,
															height: BODY_ROW_HEIGHT,
															padding: 0,
															alignItems: "center",
															justifyContent: "center",
														},
														...(bg ? [bg] : []),
													]}
												>
													<Text style={[styles.headerText, styles.textCenter]}>
														{label}
													</Text>
												</View>
											);
										})}
									</View>
								);
							})}
						</View>
					</View>
				</View>
			</View>
		</View>
	);
}
